import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { ObjectUrlUploadModal } from "./ObjectUrlUploadModal";
import configs from "../../utils/configs";
import { getCookieToken } from "../../utils/get-auth-user";
import { getSignedUrl } from "../../utils/artwork-utils";
import { guessContentType, proxiedUrlFor } from "../../utils/media-url-utils";
import { generateImageFileThumbnail, generateVideoFileThumbnail } from "../../utils/thumbnails";
import ThumbnailRenderer from "../../utils/thumbnail-renderer";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RoomLayout } from "../layout/RoomLayout";
import styles from "./ObjectUrlModal.scss";

const appName = configs.translation("app-name").toLowerCase().replace(/ /g, "-");

let loadingObject = null;

try {
  loadingObject = require(`../../assets/models/LoadingObject_${appName}.glb`);
} catch (e) {
  loadingObject = require("../../assets/models/LoadingObject_app.glb");
}

const isMobile = AFRAME.utils.device.isMobile() || AFRAME.utils.device.isMobileVR();

export function ObjectUrlUploadModalContainer({ scene, onClose, sourceNavigate, open }) {
  const [errorUrl, setErrorUrl] = useState(false);
  const onCleanUrl = () => {
    setErrorUrl(false);
  };

  function getUrlExtension(url) {
    return url.split(/[#?]/)[0].split(".").pop().trim();
  }

  async function generateFileThumbnail(file, width, height, name) {
    const thumbnailRenderer = new ThumbnailRenderer();
    const url = URL.createObjectURL(file);
    let blob;

    if (name.toLowerCase().endsWith(".glb")) {
      const { scene } = await new GLTFLoader(url).loadGLTF();
      blob = await thumbnailRenderer.generateThumbnail(scene, width, height);
    } else if ([".png", ".jpg", ".jpeg", ".gif", ".webp"].some(ext => name.toLowerCase().endsWith(ext))) {
      blob = await generateImageFileThumbnail(file);
    } else if (name.toLowerCase().endsWith(".mp4")) {
      blob = await generateVideoFileThumbnail(file);
    }

    URL.revokeObjectURL(url);

    return blob;
  }

  async function generateAndUploadThumbnail({ thumbnail, folder, token, name }) {
    const thumbnailFile = thumbnail[0];

    const signedUrl = await getSignedUrl({ file: thumbnailFile, token, folder, apiUrl: configs["CUSTOM_API_URL"] });

    await fetch(signedUrl.presigned_url, {
      method: "PUT",
      headers: {
        "Content-Type": "image/png",
        accept: "application/json",
        'x-ms-blob-type': 'BlockBlob',
      },
      body: thumbnailFile
    });

    return signedUrl.download_url;
  }


  function getMediaTypeFromMimeType(mimeType) {
    // if includes gif, then return gif
    if (mimeType.includes('gif')) {
      return 'gif';
    }

    if (mimeType.includes('image')) {
      return 'image';
    }

    if (mimeType.includes('video')) {
      return 'video';
    }

    if (mimeType.includes('audio')) {
      return 'audio';
    }

    if (mimeType.includes('pdf')) {
      return 'pdf';
    }

    if (mimeType.includes('glb')) {
      return 'model';
    }

    return '';
  }


  async function generateAndUploadThumbnailUrl({ url, folder, token, name }) {
    const responseFile = await fetch(proxiedUrlFor(url));

    const file = await responseFile.blob();

    const thumbnailBlob = await generateFileThumbnail(file, folder, token, name);

    const thumbnailFile = new File([thumbnailBlob], `${name.split(".").shift()}-thumb.png`, { type: "image/png" });

    const signedUrl = await getSignedUrl({ file: thumbnailFile, token, folder, apiUrl: configs["CUSTOM_API_URL"] });

    await fetch(signedUrl.presigned_url, {
      method: "PUT",
      headers: {
        "Content-Type": "image/png",
        accept: "application/json",
        'x-ms-blob-type': 'BlockBlob',
      },
      body: thumbnailFile
    });

    return signedUrl.download_url;
  }

  const onSubmit = useCallback(
    async ({ file, url, href, hideHref, smoothTransition = false, thumbnail, myMedia = false }) => {
      const possibleLinkExtensions = ["gif", "png", "jpg", "jpeg", "webp", "mp4", "mp3", "pdf", "glb"];
      if (myMedia && file && file.length === 0 && !possibleLinkExtensions.includes(getUrlExtension(url))) {
        setErrorUrl(true);
        return;
      }
      const token = getCookieToken();

      const folder = `assets360/${configs.APP_NAME}/my-media`;
      let fileUrl;
      if (file && file.length) {
        const acceptedFile = file[0];
        fileUrl = acceptedFile.downloableUrl;

      } else {
        const name = url.split("/").pop();

        const thumbnail = possibleLinkExtensions.includes(getUrlExtension(url))
          ? await generateAndUploadThumbnailUrl({ url, token, folder, name })
          : null;

        // if (myMedia) {
          const entry = {
            name: name,
            title: name,
            path: proxiedUrlFor(url),
            public_thumbnail: thumbnail,
            type: guessContentType(url),
            thumbnail: thumbnail,
            // isHub: true,
            url: proxiedUrlFor(url),
            asset_type: "my-media"
          };

          const myMediaUrl = `${configs["CUSTOM_API_URL"]}/assets360`;

          // send to my media
          await fetch(myMediaUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              authorization: `Bearer ${token}`
            },
            body: JSON.stringify(entry)
          });
        // }
      }

      scene.emit("add_media", {
        src: fileUrl || (file && file.length > 0 && file[0]) || url || loadingObject,
        href,
        hideHref,
        smoothTransition,
        fileIsOwned: true
      });
      onClose();
    },
    [scene, onClose]
  );

  return open ? (
    <RoomLayout
      className={styles.absolute}
      modal={
        <ObjectUrlUploadModal
          errorUrl={errorUrl}
          isMobile={isMobile}
          showModelCollectionLink={configs.feature("show_model_collection_link")}
          modelCollectionUrl={configs.link("model_collection", "https://sketchfab.com/mozillareality")}
          onSubmit={onSubmit}
          onClose={onClose}
          onCleanUrl={onCleanUrl}
          sourceNavigate={sourceNavigate}
        />
      }
    />
  ) : (
    <></>
  );
}

ObjectUrlUploadModalContainer.propTypes = {
  scene: PropTypes.object.isRequired,
  onClose: PropTypes.func
};
